import { useState, useEffect, useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import api from 'services/api'
import { useAuth } from 'store/selectors'

// The endpoint takes a user id; nobody knows a user by their ObjectId. This
// searches by username the way the Networks search bar does and hands the
// page the id, so the fleet ↔ one-install switch is a name.
//
// The fleet view carries no user ids by design, so this is the only place on
// the page a user is named at all.
//
// **A pasted ObjectId is taken as itself**, with no lookup. Two reasons, and
// the first is that the search cannot reach every account this page is for:
// it is an Atlas autocomplete over `username` and `full_name`, so a guest —
// named `guest_` plus eight random letters nobody recorded — is findable only
// by luck. The client simulator mints exactly that, and so does every
// unclaimed install in the pilot. The second is that an id is what the world
// actually hands you: a log line, a Sentry event, a support ticket and
// `stream-sim`'s own report all carry the ObjectId and never a name.
//
// No resolution call sits behind it because none is needed — every endpoint on
// this page wants the id alone — and because there is no admin route to call:
// `GET /users/{user_id}` does not exist (the users router has `/search`,
// `/username/{username}`, and the mutations). `api.users.getUser` points at it
// and 404s. Adding the route to show a name beside the id is the upgrade, not
// a prerequisite.
const OBJECT_ID = /^[0-9a-f]{24}$/i

// **You, in the list, always.** The signed-in staff account (the admin `/me`
// the app loaded at sign-in) heads the options whatever is typed — second only
// to a pasted id — so narrowing to your own phone is one click from any state
// of the box.
const UserFilter = ({ user, onChange }) => {
  const { user: me } = useAuth()
  const [input, setInput] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  const query = input.trim()
  const pastedId = OBJECT_ID.test(query)

  useEffect(() => {
    // An id is the answer, so there is nothing to search for: offering it as
    // the one option keeps the pick deliberate (Enter or a click) rather than
    // having the page jump to a new user mid-keystroke.
    if (pastedId) {
      setOptions([{ id: query.toLowerCase(), is_pasted_id: true }])
      setLoading(false)
      return undefined
    }
    if (query.length < 2) {
      setOptions([])
      return undefined
    }
    // Debounced, and guarded against out-of-order responses: a slow reply
    // for "ga" must not overwrite the results for "gavin".
    let live = true
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const users = await api.users.searchUsers(query, 0, 8)
        if (live) setOptions(users)
      } catch {
        if (live) setOptions([])
      } finally {
        if (live) setLoading(false)
      }
    }, 250)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [query, pastedId])

  const label = useMemo(
    () => (option) => option.username || option.full_name || option.id,
    []
  )

  // MUI warns when the selected value is absent from `options`, which it is
  // as soon as the search text is cleared. Keep the selection in the list.
  const mine = me?.id ? [{ id: me.id, username: me.username, is_me: true }] : []
  // A pasted id is what was asked for, so it leads; otherwise you do.
  const others = options.filter(({ id }) => id !== me?.id)
  const listed = pastedId ? [...others, ...mine] : [...mine, ...others]
  const shown =
    user && !listed.some(({ id }) => id === user.id)
      ? [user, ...listed]
      : listed

  return (
    <Autocomplete
      size="small"
      sx={{ width: 240 }}
      options={shown}
      value={user}
      loading={loading}
      onChange={(event, next) => onChange(next)}
      onInputChange={(event, next) => setInput(next)}
      getOptionLabel={label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(all) => all}
      noOptionsText={
        query.length < 2 ? 'Type a username or paste an id' : 'No users'
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.is_me ? (
            <span>
              <b>You</b>
              {option.username ? ` · ${option.username}` : ''}
            </span>
          ) : option.is_pasted_id ? (
            // Said out loud: this row was not found, it was typed. Nothing
            // confirmed the account exists, and an empty page after picking it
            // means the id was wrong rather than that the account is quiet.
            <span>
              Use id <code>{option.id}</code>
            </span>
          ) : (
            label(option)
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Fleet"
          placeholder="Username or user id"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default UserFilter
